import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { 
  ChevronLeft, Save, Calendar, MapPin, Clock, 
  Users, Euro, Loader2, User, Trash2, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from '../i18n';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categories = [
  { id: 'yoga', icon: '🧘' },
  { id: 'renforcement', icon: '💪' },
  { id: 'mobilite', icon: '🤸' },
  { id: 'outdoor', icon: '🏃' },
  { id: 'cardio', icon: '❤️' },
  { id: 'pilates', icon: '🎯' },
  { id: 'boxe', icon: '🥊' },
  { id: 'danse', icon: '💃' },
];

export default function EditSessionPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useTranslation();

  const levels = [
    { id: 'debutant', name: t('levels.beginner') },
    { id: 'intermediaire', name: t('levels.intermediate') },
    { id: 'avance', name: t('levels.advanced') },
    { id: 'tous', name: t('levels.all') },
  ];

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [session, setSession] = useState(null);
  const [participants, setParticipants] = useState([]);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    level: 'tous',
    location: '',
    start_time: '09:00',
    duration_minutes: 60,
    max_participants: 10,
    min_participants: 3,
    price: 15,
    equipment_required: '',
    indoor: false,
    date: '',
  });

  useEffect(() => {
    fetchSession();
    fetchParticipants();
  }, [sessionId]);

  const fetchSession = async () => {
    try {
      const response = await fetch(`${API_URL}/api/sessions/${sessionId}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setSession(data);
        setFormData({
          title: data.title || '',
          description: data.description || '',
          category: data.category || '',
          level: data.level || 'tous',
          location: data.location || '',
          start_time: data.start_time || '09:00',
          duration_minutes: data.duration_minutes || 60,
          max_participants: data.max_participants || 10,
          min_participants: data.min_participants || 3,
          price: data.price || 15,
          equipment_required: data.equipment_required || '',
          indoor: data.indoor || false,
          date: data.date ? data.date.split('T')[0] : '',
        });
      } else {
        toast.error(t('editSession.toastNotFound'));
        navigate('/coach/dashboard');
      }
    } catch (error) {
      console.error('Error fetching session:', error);
      toast.error(t('editSession.toastLoadError'));
    } finally {
      setLoading(false);
    }
  };

  const fetchParticipants = async () => {
    try {
      const response = await fetch(`${API_URL}/api/sessions/${sessionId}/participants`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setParticipants(data);
      }
    } catch (error) {
      console.error('Error fetching participants:', error);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updateData = {
        ...formData,
        duration_minutes: parseInt(formData.duration_minutes),
        max_participants: parseInt(formData.max_participants),
        min_participants: parseInt(formData.min_participants),
        price: parseFloat(formData.price),
      };

      const response = await fetch(`${API_URL}/api/sessions/${sessionId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updateData),
      });

      if (response.ok) {
        toast.success(t('editSession.toastUpdated'));
        navigate('/coach/dashboard');
      } else {
        const error = await response.json();
        toast.error(error.detail || t('editSession.toastUpdateError'));
      }
    } catch (error) {
      toast.error(t('editSession.toastUpdateError'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm(t('editSession.confirmDelete'))) return;

    try {
      const response = await fetch(`${API_URL}/api/sessions/${sessionId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        toast.success(t('editSession.toastDeleted'));
        navigate('/coach/dashboard');
      } else {
        toast.error(t('editSession.toastDeleteError'));
      }
    } catch (error) {
      toast.error(t('editSession.toastDeleteError'));
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const locale = lang === 'nl' ? 'nl-BE' : lang === 'en' ? 'en-GB' : 'fr-FR';
    return date.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate(-1)} className="text-muted-foreground hover:text-foreground">
                <ChevronLeft className="w-6 h-6" />
              </button>
              <span className="font-bold uppercase" data-testid="edit-session-title">{t('editSession.title')}</span>
            </div>
            <Link to="/">
              <img src="/logo.png" alt="Motivya" className="h-8 w-auto" />
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {session && (
          <div className="mb-6 flex items-center gap-4">
            <span className={`px-3 py-1 rounded text-sm font-medium ${
              session.status === 'confirmed' ? 'badge-confirmed' : 
              session.status === 'cancelled' ? 'badge-cancelled' : 'badge-pending'
            }`}>
              {session.status === 'confirmed' ? t('editSession.confirmed') : 
               session.status === 'cancelled' ? t('editSession.cancelled') : t('editSession.pending')}
            </span>
            <span className="text-muted-foreground text-sm">
              {session.current_participants}/{session.max_participants} {t('editSession.participants')}
            </span>
            <Link to={`/session/${sessionId}`} className="text-primary text-sm hover:underline flex items-center gap-1">
              <Eye className="w-4 h-4" />
              {t('editSession.viewPage')}
            </Link>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-card border border-border rounded-lg p-6 space-y-4">
              <h3 className="font-bold uppercase">{t('editSession.generalInfo')}</h3>
              
              <div className="space-y-2">
                <Label htmlFor="title">{t('editSession.sessionTitle')}</Label>
                <Input id="title" value={formData.title} onChange={(e) => handleChange('title', e.target.value)} required data-testid="edit-session-title-input" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">{t('editSession.description')}</Label>
                <Textarea id="description" value={formData.description} onChange={(e) => handleChange('description', e.target.value)} rows={4} required data-testid="edit-session-description" />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('editSession.category')}</Label>
                  <Select value={formData.category} onValueChange={(v) => handleChange('category', v)}>
                    <SelectTrigger data-testid="edit-session-category"><SelectValue placeholder={t('editSession.select')} /></SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.icon} {t(`categories.${cat.id}`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t('editSession.level')}</Label>
                  <Select value={formData.level} onValueChange={(v) => handleChange('level', v)}>
                    <SelectTrigger data-testid="edit-session-level"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {levels.map((lvl) => (<SelectItem key={lvl.id} value={lvl.id}>{lvl.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6 space-y-4">
              <h3 className="font-bold uppercase">{t('editSession.dateTime')}</h3>
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="date">{t('editSession.date')}</Label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input id="date" type="date" value={formData.date} onChange={(e) => handleChange('date', e.target.value)} className="pl-10" required data-testid="edit-session-date" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="start_time">{t('editSession.startTime')}</Label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input id="start_time" type="time" value={formData.start_time} onChange={(e) => handleChange('start_time', e.target.value)} className="pl-10" required data-testid="edit-session-time" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{t('editSession.duration')}</Label>
                  <Select value={String(formData.duration_minutes)} onValueChange={(v) => handleChange('duration_minutes', v)}>
                    <SelectTrigger data-testid="edit-session-duration"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="30">{t('editSession.dur30')}</SelectItem>
                      <SelectItem value="45">{t('editSession.dur45')}</SelectItem>
                      <SelectItem value="60">{t('editSession.dur60')}</SelectItem>
                      <SelectItem value="90">{t('editSession.dur90')}</SelectItem>
                      <SelectItem value="120">{t('editSession.dur120')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6 space-y-4">
              <h3 className="font-bold uppercase">{t('editSession.location')}</h3>
              <div className="space-y-2">
                <Label htmlFor="location">{t('editSession.address')}</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input id="location" value={formData.location} onChange={(e) => handleChange('location', e.target.value)} className="pl-10" required data-testid="edit-session-location" />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="indoor">{t('editSession.indoor')}</Label>
                <Switch id="indoor" checked={formData.indoor} onCheckedChange={(v) => handleChange('indoor', v)} data-testid="edit-session-indoor" />
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6 space-y-4">
              <h3 className="font-bold uppercase">{t('editSession.participantsAndPricing')}</h3>
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="min">{t('editSession.minParticipants')}</Label>
                  <Input id="min" type="number" min={1} value={formData.min_participants} onChange={(e) => handleChange('min_participants', e.target.value)} data-testid="edit-session-min" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="max">{t('editSession.maxParticipants')}</Label>
                  <Input id="max" type="number" min={1} value={formData.max_participants} onChange={(e) => handleChange('max_participants', e.target.value)} data-testid="edit-session-max" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="price">{t('editSession.pricePerPerson')}</Label>
                  <div className="relative">
                    <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input id="price" type="number" min={0} step={0.5} value={formData.price} onChange={(e) => handleChange('price', e.target.value)} className="pl-10" data-testid="edit-session-price" />
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="equipment">{t('editSession.equipment')}</Label>
                <Input id="equipment" value={formData.equipment_required} onChange={(e) => handleChange('equipment_required', e.target.value)} data-testid="edit-session-equipment" />
              </div>
            </div>

            <div className="flex gap-4">
              <Button variant="destructive" onClick={handleDelete} className="flex-shrink-0" data-testid="delete-session">
                <Trash2 className="w-4 h-4 mr-2" />
                {t('editSession.cancelSession')}
              </Button>
              <div className="flex gap-4 ml-auto">
                <Button variant="outline" onClick={() => navigate(-1)}>{t('editSession.back')}</Button>
                <Button className="btn-primary" onClick={handleSave} disabled={saving} data-testid="save-session">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  {t('editSession.save')}
                </Button>
              </div>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-card border border-border rounded-lg p-6 sticky top-24">
              <h3 className="font-bold uppercase mb-4">{t('editSession.enrolledParticipants')}</h3>
              {participants.length === 0 ? (
                <p className="text-muted-foreground text-sm">0 {t('editSession.participants')}</p>
              ) : (
                <div className="space-y-3">
                  {participants.map((participant, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 bg-muted/20 rounded-lg">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <User className="w-4 h-4 text-primary" />
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-sm">{participant.name || t('editSession.user')}</div>
                        <div className="text-xs text-muted-foreground">{participant.email}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
